import {
  EnvironmentProviders,
  effect,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core'
import { LinguiService } from '@tocdk/lingui-angular'
import { Translation } from 'primeng/api'
import { PrimeNG } from 'primeng/config'

export type PrimeNgLocale = 'en' | 'da'

/**
 * PrimeNG built-in strings (date picker day/month names, Today/Clear, week
 * header, aria labels) per app locale (#2531). Weeks start on Monday in both
 * locales; `dateFormat` matches the default profile format (DD-MM-YYYY). It
 * also carries the select/listbox empty-state text (`emptyMessage`,
 * `emptyFilterMessage`, #2945); `en` sets them explicitly because
 * `PrimeNG.setTranslation` merges (top level only, so each locale carries the
 * COMPLETE `aria` object, never a partial one), so a da→en switch would otherwise keep the
 * Danish text.
 */
export const PRIMENG_TRANSLATIONS: Record<PrimeNgLocale, Translation> = {
  en: {
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    today: 'Today',
    clear: 'Clear',
    weekHeader: 'Wk',
    firstDayOfWeek: 1,
    dateFormat: 'dd-mm-yy',
    chooseDate: 'Choose date',
    chooseMonth: 'Choose month',
    chooseYear: 'Choose year',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    prevYear: 'Previous year',
    nextYear: 'Next year',
    prevDecade: 'Previous decade',
    nextDecade: 'Next decade',
    emptyMessage: 'No results found',
    emptyFilterMessage: 'No results found',
    aria: {
        trueLabel: 'True',
        falseLabel: 'False',
        nullLabel: 'Not Selected',
        star: '1 star',
        stars: '{star} stars',
        selectAll: 'All items selected',
        unselectAll: 'All items unselected',
        close: 'Close',
        previous: 'Previous',
        next: 'Next',
        navigation: 'Navigation',
        scrollTop: 'Scroll Top',
        moveTop: 'Move Top',
        moveUp: 'Move Up',
        moveDown: 'Move Down',
        moveBottom: 'Move Bottom',
        moveToTarget: 'Move to Target',
        moveToSource: 'Move to Source',
        moveAllToTarget: 'Move All to Target',
        moveAllToSource: 'Move All to Source',
        pageLabel: '{page}',
        firstPageLabel: 'First Page',
        lastPageLabel: 'Last Page',
        nextPageLabel: 'Next Page',
        prevPageLabel: 'Previous Page',
        rowsPerPageLabel: 'Rows per page',
        previousPageLabel: 'Previous Page',
        jumpToPageDropdownLabel: 'Jump to Page Dropdown',
        jumpToPageInputLabel: 'Jump to Page Input',
        selectRow: 'Row Selected',
        unselectRow: 'Row Unselected',
        expandRow: 'Row Expanded',
        collapseRow: 'Row Collapsed',
        showFilterMenu: 'Show Filter Menu',
        hideFilterMenu: 'Hide Filter Menu',
        filterOperator: 'Filter Operator',
        filterConstraint: 'Filter Constraint',
        editRow: 'Row Edit',
        saveEdit: 'Save Edit',
        cancelEdit: 'Cancel Edit',
        listView: 'List View',
        gridView: 'Grid View',
        slide: 'Slide',
        slideNumber: '{slideNumber}',
        zoomImage: 'Zoom Image',
        zoomIn: 'Zoom In',
        zoomOut: 'Zoom Out',
        rotateRight: 'Rotate Right',
        rotateLeft: 'Rotate Left',
        listLabel: 'Option List',
        selectColor: 'Select a color',
        removeLabel: 'Remove',
        browseFiles: 'Browse Files',
        maximizeLabel: 'Maximize',
        minimizeLabel: 'Minimize',
    },
  },
  da: {
    dayNames: ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'],
    dayNamesShort: ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'],
    dayNamesMin: ['sø', 'ma', 'ti', 'on', 'to', 'fr', 'lø'],
    monthNames: [
      'januar', 'februar', 'marts', 'april', 'maj', 'juni',
      'juli', 'august', 'september', 'oktober', 'november', 'december',
    ],
    monthNamesShort: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
    today: 'I dag',
    clear: 'Ryd',
    weekHeader: 'Uge',
    firstDayOfWeek: 1,
    dateFormat: 'dd-mm-yy',
    chooseDate: 'Vælg dato',
    chooseMonth: 'Vælg måned',
    chooseYear: 'Vælg år',
    prevMonth: 'Forrige måned',
    nextMonth: 'Næste måned',
    prevYear: 'Forrige år',
    nextYear: 'Næste år',
    prevDecade: 'Forrige årti',
    nextDecade: 'Næste årti',
    emptyMessage: 'Ingen resultater',
    emptyFilterMessage: 'Ingen resultater',
    aria: {
      trueLabel: 'Sand',
      falseLabel: 'Falsk',
      nullLabel: 'Ikke valgt',
      star: '1 stjerne',
      stars: '{star} stjerner',
      selectAll: 'Alle elementer valgt',
      unselectAll: 'Alle elementer fravalgt',
      close: 'Luk',
      previous: 'Forrige',
      next: 'Næste',
      navigation: 'Navigation',
      scrollTop: 'Rul til toppen',
      moveTop: 'Flyt til toppen',
      moveUp: 'Flyt op',
      moveDown: 'Flyt ned',
      moveBottom: 'Flyt til bunden',
      moveToTarget: 'Flyt til mål',
      moveToSource: 'Flyt til kilde',
      moveAllToTarget: 'Flyt alle til mål',
      moveAllToSource: 'Flyt alle til kilde',
      pageLabel: 'Side {page}',
      firstPageLabel: 'Første side',
      lastPageLabel: 'Sidste side',
      nextPageLabel: 'Næste side',
      prevPageLabel: 'Forrige side',
      rowsPerPageLabel: 'Rækker pr. side',
      jumpToPageDropdownLabel: 'Gå til side',
      jumpToPageInputLabel: 'Gå til side',
      selectRow: 'Række valgt',
      unselectRow: 'Række fravalgt',
      expandRow: 'Række udvidet',
      collapseRow: 'Række skjult',
      showFilterMenu: 'Vis filtermenu',
      hideFilterMenu: 'Skjul filtermenu',
      filterOperator: 'Filteroperator',
      filterConstraint: 'Filterbetingelse',
      editRow: 'Rediger række',
      saveEdit: 'Gem ændring',
      cancelEdit: 'Annuller ændring',
      listView: 'Listevisning',
      gridView: 'Gittervisning',
      slide: 'Slide',
      slideNumber: '{slideNumber}',
      zoomImage: 'Zoom billede',
      zoomIn: 'Zoom ind',
      zoomOut: 'Zoom ud',
      rotateRight: 'Roter til højre',
      rotateLeft: 'Roter til venstre',
      previousPageLabel: 'Forrige side',
      listLabel: 'Liste over valgmuligheder',
      selectColor: 'Vælg en farve',
      removeLabel: 'Fjern',
      browseFiles: 'Gennemse filer',
      maximizeLabel: 'Maksimer',
      minimizeLabel: 'Minimer',
    },
  },
}

function translationFor(locale: string): Translation {
  return PRIMENG_TRANSLATIONS[locale as PrimeNgLocale] ?? PRIMENG_TRANSLATIONS.en
}

/**
 * Keeps PrimeNG's translation in step with the active Lingui locale. Register
 * after `providePrimeNG(...)`.
 */
export function providePrimeNgLocale(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    const lingui = inject(LinguiService)
    const primeng = inject(PrimeNG)
    effect(() => primeng.setTranslation(translationFor(lingui.locale())))
  })
}
